import { Questionnaire } from '../types';

export const onboardingQuestionnaire: Questionnaire = {
  sections: [
    {
      id: 'introduction',
      title: 'Introduction',
      questions: [
        {
          id: 'q1',
          type: 'text',
          question: 'What should we call you?',
          label: 'Your Name',
          onboardingField: 'name',
        },
        {
          id: 'q2',
          type: 'single-select',
          question: 'How do you identify?',
          options: [
            { label: 'Female', value: 'Female' },
            { label: 'Male', value: 'Male' },
            { label: 'Non-binary', value: 'Non-binary' },
            { label: 'Prefer not to say', value: 'Prefer not to say' },
            { label: 'Other', value: 'Other', additionalInfo: true },
          ],
          onboardingField: 'gender',
        },
        {
          id: 'q3',
          type: 'date',
          question: "When's your birthday?",
          label: 'Your Date of Birth',
          onboardingField: 'dob',
          maxDate: new Date(
            new Date().getFullYear() - 18,
            new Date().getMonth(),
            new Date().getDate(),
          ),
        },
        {
          id: 'q4',
          type: 'single-select',
          question: 'Which ethnicity best describes you?',
          options: [
            { label: 'White', value: 'White' },
            {
              label: 'Black or African Descent',
              value: 'Black or African Descent',
            },
            { label: 'Asian', value: 'Asian' },
            { label: 'Hispanic or Latino', value: 'Hispanic or Latino' },
            { label: 'Mixed or Multiracial', value: 'Mixed/Multiracial' },
            { label: 'Indigenous or Native', value: 'Indigenous or Native' },
            {
              label: 'Middle Eastern or North African',
              value: 'Middle Eastern or North African',
            },
            { label: 'Prefer not to say', value: 'Prefer not to say' },
            { label: 'Other', value: 'other', additionalInfo: true },
          ],
          onboardingField: 'ethnicity',
        },
        {
          id: 'q5',
          type: 'country',
          question: 'Where are you living right now?',
          label: 'Country of Residence',
          onboardingField: 'country',
        },
        {
          id: 'q6',
          type: 'number',
          question: "What's your height?",
          label: 'Height',
          unit: 'cm',
          min: 100,
          max: 250,
          onboardingField: 'height',
        },
        {
          id: 'q7',
          type: 'number',
          question: "What's your current weight?",
          label: 'Weight',
          unit: 'kg',
          min: 40,
          max: 200,
          onboardingField: 'weight',
        },
        {
          id: 'intro_info',
          type: 'info',
          title: "That's a great start!",
          content:
            "Remember, the weighing scale isn't a measure of your self-worth or health. We are just here to learn more about you!",
          helperText: "Next, let's explore your health history.",
        },
      ],
    },
    {
      id: 'diagnosis',
      title: 'Diagnosis & Symptoms',
      questions: [
        {
          id: 'q8',
          type: 'multi-select',
          question: 'Have you ever been diagnosed with any of the following?',
          helperText: 'Select all that apply',
          submitGroup: 'diagnoses',
          submitOnComplete: false,
          options: [
            { label: 'PCOS', value: 'pcos' },
            { label: 'Menopause', value: 'menopause' },
            { label: 'Fibroids', value: 'fibroids' },
            { label: 'Endometriosis', value: 'endometriosis' },
            { label: 'Perimenopause', value: 'perimenopause' },
            {
              label:
                "No, but I think I have a women's health condition for which I require a diagnosis",
              value: 'no_diagnosis',
            },
            {
              label: "No, I'm healthy and here to optimise my health",
              value: 'healthy',
            },
          ],
        },
        {
          id: 'q9',
          type: 'multi-select',
          question:
            "Besides a diagnosis, have you noticed any symptoms related to the following women's health conditions?",
          helperText: 'Select all that apply',
          showIf: { q8: ['no_diagnosis'] },
          options: [
            { label: 'PCOS', value: 'pcos' },
            { label: 'Menopause', value: 'menopause' },
            { label: 'Fibroids', value: 'fibroids' },
            { label: 'Endometriosis', value: 'endometriosis' },
            { label: 'Perimenopause', value: 'perimenopause' },
            { label: 'No', value: null },
          ],
          onboardingField: 'observedSymptomForConditions',
        },
        {
          id: 'q10',
          type: 'single-select',
          question: 'Who provided you with a formal diagnosis?',
          repeatFor: 'q8',
          excludeValues: ['no_diagnosis', 'healthy'],
          submitGroup: 'diagnoses',
          options: [
            {
              label: 'General Practitioner / Internal Medicine Specialist',
              value: 'General Practitioner / Internal Medicine Specialist',
            },
            {
              label: 'Obstetrician / Gynecologist',
              value: 'Obstetrician / Gynecologist',
            },
            { label: 'Endocrinologist', value: 'Endocrinologist' },
            {
              label: 'Reproductive Specialist (Fertility Specialist)',
              value: 'Reproductive Specialist (Fertility Specialist)',
            },
            { label: 'Other', value: 'other', additionalInfo: true },
          ],
        },
        {
          id: 'q11',
          type: 'number',
          question: 'At what age were you diagnosed?',
          label: 'Age',
          min: 0,
          max: 100,
          repeatFor: 'q8',
          excludeValues: ['no_diagnosis', 'healthy'],
          submitGroup: 'diagnoses',
        },
        {
          id: 'q12',
          type: 'multi-select',
          question: 'What management were you provided at diagnosis?',
          repeatFor: 'q8',
          excludeValues: ['no_diagnosis', 'healthy'],
          submitGroup: 'diagnoses',
          options: [
            { label: 'Medications', value: 'medication' },
            { label: 'Supplements', value: 'supplement' },
            {
              label: 'Comprehensive lifestyle advice',
              value: 'comprehensive_lifestyle_advice',
            },
            { label: 'Surgical treatment', value: 'surgical_treatment' },
            { label: 'Physical therapy', value: 'physical_therapy' },
            {
              label: 'Counselling or therapy',
              value: 'counselling_therapy',
            },
            {
              label: 'No management provided',
              value: 'No management provided',
            },
            {
              label: 'Lifestyle modifications',
              value: 'life_style_modifications',
              description: 'diet, exercise, etc.',
            },
            {
              label: 'Other',
              value: 'other',
              additionalInfo: true,
            },
          ],
        },
        {
          id: 'q13',
          type: 'slider',
          question:
            'How severely does your health affect your day-to-day life?',
          helperText:
            'Please rate from 1 to 10, where:\n1 = No impact on daily activities\n10 = Complete inability to carry out daily tasks',
          min: 0,
          max: 10,
          minLabel: 'No impact',
          maxLabel: 'Complete inability',
          showIf: { q8: { not: ['healthy'] } },
        },
        {
          id: 'q14',
          type: 'multi-select',
          question: 'Do you experience any of the following symptoms?',
          helperText: 'Select all that apply',
          submitGroup: 'symptoms',
          submitOnComplete: false,
          loadOptionsFromApi: 'symptoms',
          options: [{ label: 'None of the above', value: null }],
        },
        {
          id: 'q14-detail',
          type: 'group-detail',
          question:
            "Please rate the severity of your symptoms and how long you've been experiencing them",
          showIf: { q14: { not: null } },
          dependsOn: 'q14',
          label: 'Duration',
          submitGroup: 'symptoms',
          options: [
            { label: 'Under 1 month', value: 'under_one_month' },
            { label: '2-6 months', value: 'two_to_six_months' },
            { label: 'Over 6 months', value: 'over_six_months' },
          ],
          showSeveritySlider: true,
        },
        {
          id: 'diagnosis_cycle',
          type: 'info',
          content:
            'Your past experiences shape your future and so thank you for sharing them with us!',
          helperText: "Now, let's dive into that menstrual cycle.",
          showIf: { q8: { not: ['menopause'] } },
        },
        {
          id: 'diagnosis_nocycle',
          type: 'info',
          content:
            'Your past experiences shape your future and so thank you for sharing them with us!',
          helperText:
            "Now, we'd love to learn more about any other health concerns, your nutrition habits, and how everything comes together. This will allow us to create a plan that truly works for you.",
          showIf: { q8: ['menopause'] },
        },
      ],
    },
    {
      id: 'cycle',
      title: 'Menstrual Cycle',
      questions: [
        {
          id: 'q15',
          type: 'single-select',
          question: 'Tell us about your menstrual cycle',
          helperText:
            'A regular cycle is generally considered to be between 21 and 35 days',
          showIf: { q8: { not: ['menopause'] } },
          questionSlug: 'menstrual_cycle',
          options: [
            {
              label: 'My cycle is regular without any medication',
              value: 'My cycle is regular without any medication',
              answerSlug: 'regular_cycle',
            },
            {
              label: "My cycle is regular because I'm on the pill",
              value: "My cycle is regular because I'm on the pill",
              answerSlug: 'regular_cycle_on_pill',
            },
            {
              label: 'My cycle is irregular',
              description: 'More than 35 days or fewer than 8 periods per year',
              value:
                'My cycle is irregular. More than 35 days or fewer than 8 periods per year',
              answerSlug: 'irregular_cycle',
            },
            {
              label:
                'My cycle is irregular, but I take medication (e.g., the pill) to regulate it',
              value:
                'My cycle is irregular, but I take medication (e.g., the pill) to regulate it',
              answerSlug: 'irregular_cycle_medicated',
            },
            {
              label: 'I am currently pregnant or breastfeeding',
              value: 'I am currently pregnant or breastfeeding',
              answerSlug: 'pregnant_or_breastfeeding',
            },
            {
              label: 'I have not had a period for more than 12 months',
              value: 'I have not had a period for more than 12 months',
              answerSlug: 'no_period_for_more_than_12_months',
            },
          ],
        },
        {
          id: 'q16',
          type: 'number',
          question: 'How many days does your period typically last?',
          label: 'Days',
          showIf: { q8: { not: ['menopause'] } },
        },
        {
          id: 'q17',
          type: 'single-select',
          question: 'Describe the bleeding?',
          showIf: { q8: { not: ['menopause'] } },
          questionSlug: 'menstrual_bleeding',
          options: [
            { label: 'Spotting', value: 'Spotting', answerSlug: 'spotting' },
            {
              label: 'Light bleeding',
              value: 'Light bleeding',
              answerSlug: 'light_bleeding',
            },
            {
              label: 'Moderate bleeding',
              value: 'Moderate bleeding',
              answerSlug: 'moderate_bleeding',
            },
            {
              label: 'Heavy bleeding with clots',
              value: 'Heavy bleeding with clots',
              answerSlug: 'heavy_bleeding_clots',
            },
            {
              label: 'Very heavy bleeding',
              value: 'Very heavy bleeding',
              answerSlug: 'very_heavy_bleeding',
            },
          ],
        },
        {
          id: 'q18',
          type: 'multi-select',
          question:
            'Do you experience any of the following symptoms around your period?',
          helperText: 'Select all that apply',
          showIf: { q8: { not: ['menopause'] } },
          onboardingField: 'symptomsAroundPeriod',
          loadOptionsFromApi: 'symptoms',
          options: [{ label: 'No symptoms', value: null }],
        },
        {
          id: 'q19',
          type: 'date',
          question: 'When did your last period start?',
          showIf: { q8: { not: ['menopause'] } },
          label: null,
          onboardingField: 'lastPeriodStartedAt',
          canSkipQuestion: true,
          maxDate: new Date(),
        },
        {
          id: 'cycle_info',
          type: 'info',
          title: "You're halfway there!",
          content: "Menstrual health is the backbone of women's health!",
          helperText:
            "We are working hard to tailor your program and needs with the information you've provided. We need a few more details to help us do so; let's talk about contraception.",
          showIf: { q8: { not: ['menopause'] } },
        },
      ],
    },
    {
      id: 'contraception',
      title: 'Contraception',
      questions: [
        {
          id: 'q20',
          type: 'multi-select',
          question:
            'What forms of contraception have you used or are you currently using?',
          helperText: 'Select all that apply',
          showIf: { q8: { not: ['menopause'] } },
          submitGroup: 'contraceptions',
          submitOnComplete: false,
          options: [
            {
              label: 'The pill',
              value: 'pill',
              isUsingNow: true,
            },
            { label: 'IUD', value: 'iud', isUsingNow: true },
            {
              label: 'Condom',
              value: 'condom',
              isUsingNow: true,
            },
            {
              label: 'Withdrawal',
              value: 'withdrawal',
              isUsingNow: true,
            },
            {
              label: 'Vasectomy',
              value: 'vasectomy',
              isUsingNow: true,
            },
            {
              label: 'Herbal or supplements',
              value: 'herbal_supplement',
              isUsingNow: true,
            },
            {
              label: 'Been on the pill in the past',
              value: 'been_on_pill_in_past',
            },
            {
              label: 'None - I am sexually active',
              value: 'none_sexually_active',
              isUsingNow: true,
            },
            {
              label: 'None - I am trying to conceive',
              value: 'none_trying_to_conceive',
              isUsingNow: true,
            },
            {
              label: 'None - I am currently not sexually active',
              value: 'none_not_sexually_active',
              isUsingNow: true,
            },
          ],
        },
        {
          id: 'q21',
          type: 'number',
          question: 'How long have you been using it?',
          repeatFor: 'q20',
          excludeValues: [
            'none_sexually_active',
            'none_trying_to_conceive',
            'none_not_sexually_active',
            'condom',
            'withdrawal',
            'vasectomy',
          ],
          label: 'Time in Months',
          unit: 'months',
          submitGroup: 'contraceptions',
        },
        {
          id: 'q22',
          type: 'date',
          question: 'When did you stop using it?',
          repeatFor: 'q20',
          excludeValues: [
            'none_sexually_active',
            'none_trying_to_conceive',
            'none_not_sexually_active',
            'condom',
            'withdrawal',
            'vasectomy',
          ],
          label: 'Month/Year',
          showIf: {
            q20: {
              not: [
                'none_sexually_active',
                'none_trying_to_conceive',
                'none_not_sexually_active',
              ],
            },
          },
          submitGroup: 'contraceptions',
          maxDate: new Date(),
        },
        {
          id: 'q23',
          type: 'multi-select',
          question: 'Did it trigger any symptoms?',
          helperText: 'Select all that apply',
          repeatFor: 'q20',
          excludeValues: [
            'none_sexually_active',
            'none_trying_to_conceive',
            'none_not_sexually_active',
            'condom',
            'withdrawal',
            'vasectomy',
          ],
          submitGroup: 'contraceptions',
          loadOptionsFromApi: 'symptoms',
          options: [
            { label: 'Other', value: 'other', additionalInfo: true },
            { label: 'None of the above', value: null },
          ],
        },
        {
          id: 'q24',
          type: 'single-select',
          question: 'Are you currently looking to get pregnant?',
          onboardingField: 'pregnancyIntent',
          showIf: { q8: { not: ['menopause'] } },
          questionSlug: 'pregnancy_intent',
          options: [
            {
              label: 'Yes, actively trying to conceive',
              value: 'yes',
              answerSlug: 'actively_trying',
            },
            {
              label:
                'No, not trying to get pregnant or not interested in pregnancy at this time',
              value: 'no',
              answerSlug: 'not_trying',
            },
            {
              label: 'Not actively trying, but open to the idea',
              value: 'not_trying_but_open_to_idea',
              answerSlug: 'open_to_pregnancy',
            },
            {
              label: 'Not sure yet or still considering',
              value: 'not_sure_or_still_considering',
              answerSlug: 'considering_pregnancy',
            },
          ],
        },
        {
          id: 'contraception_info',
          type: 'info',
          title: "You're almost there!",
          content: 'Thank you for being honest with your answers.',
          helperText:
            "Beyond women's health, we'd love to learn more about any other health concerns, your nutrition habits, and how everything comes together. This will allow us to create a plan that truly works for you.",
          showIf: { q8: { not: ['menopause'] } },
        },
      ],
    },
    {
      id: 'other_health',
      title: 'Other Health Conditions',
      questions: [
        {
          id: 'q25',
          type: 'multi-select',
          question:
            'Have you been diagnosed with any of the following health conditions?',
          helperText: 'Select all that apply',
          onboardingField: 'generalHealthConditions',
          questionSlug: 'health_conditions',
          options: [
            {
              label: 'Type 2 diabetes',
              value: 'Type 2 diabetes',
              answerSlug: 'type_2_diabetes',
            },
            {
              label: 'Type 1 diabetes',
              value: 'Type 1 diabetes',
              answerSlug: 'type_1_diabetes',
            },
            {
              label: 'Heart disease',
              value: 'Heart disease',
              description: 'Ischemic, heart failure',
              answerSlug: 'heart_disease',
            },
            {
              label: 'High blood pressure',
              value: 'High blood pressure',
              description: 'Hypertension',
              answerSlug: 'high_blood_pressure',
            },
            {
              label: 'High cholesterol',
              value: 'High cholesterol',
              description: 'Hyperlipidaemia',
              answerSlug: 'high_cholesterol',
            },
            {
              label: 'Sexually transmitted infections (STIs)',
              value: 'Sexually transmitted infections (STIs)',
              answerSlug: 'sexually_transmitted_infections',
            },
            {
              label: 'Osteoporosis',
              value: 'Osteoporosis',
              description: 'Very low bone density',
              answerSlug: 'osteoporosis',
            },
            {
              label: 'Osteopenia',
              value: 'Osteopenia',
              description: 'Low bone density',
              answerSlug: 'osteopenia',
            },
            {
              label: 'Thyroid issues',
              value: 'Thyroid issues',
              description: 'Hypo/hyperthyroidism',
              answerSlug: 'thyroid_issues',
            },
            {
              label: 'Obesity',
              value: 'Obesity',
              description: 'Overweight',
              answerSlug: 'obesity',
            },
            {
              label: 'Chronic kidney disease',
              value: 'Chronic kidney disease',
              answerSlug: 'chronic_kidney_disease',
            },
            {
              label: 'Cancer',
              value: 'Cancer',
              answerSlug: 'cancer',
            },
            {
              label: 'Active infections',
              value: 'Active infections',
              description: 'e.g., Flu, COVID-19, or other active infections',
              answerSlug: 'active_infections',
            },
            {
              label: 'Prediabetes or insulin resistance',
              value: 'Prediabetes or insulin resistance',
              answerSlug: 'prediabetes_or_insulin_resistance',
            },
            {
              label: 'Fatty liver disease',
              value: 'Fatty liver disease',
              description: 'Non-alcoholic fatty liver disease',
              answerSlug: 'fatty_liver_disease',
            },
            {
              label: 'Other',
              value: 'other',
              additionalInfo: true,
              answerSlug: 'other_health_condition',
            },
            {
              label: 'None of the above',
              value: null,
            },
          ],
        },
      ],
    },
    {
      id: 'nutrition',
      title: 'Nutrition Assessment',
      questions: [
        {
          id: 'q26',
          type: 'multi-select',
          question: 'What types of recipes would you like to see?',
          helperText: 'Select all that apply',
          onboardingField: 'recipePreferences',
          loadOptionsFromApi: 'recipes',
        },
        {
          id: 'q27',
          type: 'multi-select',
          question:
            'Do you suffer from any of the following symptoms after eating a meal?',
          helperText: 'Select all that apply',
          onboardingField: 'symptomsAfterMeal',
          loadOptionsFromApi: 'symptoms',
          options: [
            {
              label: 'No, I do not experience any of these',
              value: null,
            },
          ],
        },
        {
          id: 'q28',
          type: 'single-select',
          question: 'Do your emotions affect the way you eat?',
          questionSlug: 'emotions_affects_eating',
          options: [
            {
              label: 'Yes, I eat differently based on my mood',
              value: 'Yes, I eat differently based on my mood',
              answerSlug: 'yes_emotions_affect_eating',
            },
            {
              label: "No, my emotions don't affect my eating habits",
              value: "No, my emotions don't affect my eating habits",
              answerSlug: 'no_emotions_affect_eating',
            },
          ],
        },
        {
          id: 'q29',
          type: 'single-select',
          question:
            'Does the number on the weighing scale impact your mood for the rest of the day?',
          helperText: 'Positively or negatively',
          questionSlug: 'weight_affects_mood',
          options: [
            {
              label: 'Yes',
              value: 'Yes',
              answerSlug: 'yes_weight_affects_mood',
            },
            {
              label: 'No',
              value: 'No',
              answerSlug: 'no_weight_affects_mood',
            },
          ],
        },
        {
          id: 'q30',
          type: 'single-select',
          question: 'Are you happy with the way you look?',
          questionSlug: 'body_satisfaction',
          options: [
            {
              label: 'Yes',
              value: 'Yes',
              answerSlug: 'yes_body_satisfaction',
            },
            { label: 'No', value: 'No', answerSlug: 'no_body_satisfaction' },
            {
              label: "Neutral / I'm Indifferent",
              value: "Neutral / I'm Indifferent",
              answerSlug: 'neutral_body_satisfaction',
            },
          ],
        },
      ],
    },
    {
      id: 'health_goals',
      title: 'Health Goals',
      questions: [
        {
          id: 'q31',
          type: 'multi-select',
          question: 'What are your current health goals?',
          helperText: 'Select all that apply',
          options: [
            {
              label: "Improving education on women's health",
              value: "Improving education on women's health",
            },
            {
              label: 'Managing stress or improving mental well-being',
              value: 'Managing stress or improving mental well-being',
            },
            {
              label: 'Improving sleep quality',
              value: 'Improving sleep quality',
            },
            {
              label: 'Eating a more nutritious and well-balanced diet',
              value: 'Eating a more nutritious and well-balanced diet',
            },
            {
              label: 'Improving energy levels',
              value: 'Improving energy levels',
            },
            { label: 'Managing weight', value: 'Managing weight' },
            {
              label: 'Being part of a community of like-minded women',
              value: 'Being part of a community of like-minded women',
            },
            { label: 'Other', value: 'Other', additionalInfo: true },
          ],
        },
      ],
    },
  ],
};

export default onboardingQuestionnaire;
